/**
 * API service for communicating with the admin panel backend
 */
import axios from "axios";
import { getConfig } from "@/config";
import {
  Script,
  ScriptCreate,
  ScriptUpdate,
  ScriptListResponse,
  ScriptFilters,
  VersionControlAction,
  ScriptDuplicateRequest,
  ScriptApproveRequest,
  ScriptRejectRequest,
} from "../types/script";
import {
  Variable,
  VariableCreate,
  VariableUpdate,
  VariableListResponse,
  VariableFilters,
} from "../types/variable";
import {
  Client,
  ClientCreate,
  ClientFilters,
  ClientListResponse,
  ClientUpdate,
} from "../types/client";
import {
  Campaign,
  CampaignCreate,
  CampaignFilters,
  CampaignListResponse,
  CampaignUpdate,
} from "../types/campaign";
import {
  QuietPeriod,
  QuietPeriodCreate,
  QuietPeriodFilters,
  QuietPeriodListResponse,
  QuietPeriodUpdate,
} from "../types/quiet_period";
import {
  HttpRequestConfig,
  HttpRequestConfigCreate,
  HttpRequestConfigFilters,
  HttpRequestConfigListResponse,
  HttpRequestConfigUpdate,
  HttpRequestEndpoint,
  HttpRequestEndpointCreate,
  HttpRequestEndpointFilters,
  HttpRequestEndpointListResponse,
  HttpRequestEndpointUpdate,
} from "../types/http";

// Create axios instance with base configuration
const { VITE_API_URL, VITE_DEBUG } = getConfig();

const api = axios.create({
  baseURL: `${VITE_API_URL}/api/v1`,
  timeout: 3000000,
  headers: {
    "Content-Type": "application/json",
  },
});

// Dedicated client for auth refresh without interceptors/Authorization header
const refreshClient = axios.create({
  baseURL: api.defaults.baseURL,
  timeout: 3000000,
  headers: { "Content-Type": "application/json" },
});

// Ensure only one refresh runs at a time
let refreshInFlight: Promise<string> | null = null;

const performTokenRefresh = (refreshToken: string): Promise<string> => {
  if (!refreshInFlight) {
    refreshInFlight = refreshClient
      .post("/auth/refresh", { refresh_token: refreshToken })
      .then((res) => {
        const newAccessToken = res.data?.access_token as string | undefined;
        if (!newAccessToken) {
          throw new Error("No access token in refresh response");
        }
        // Persist
        if (typeof window !== "undefined") {
          localStorage.setItem("auth_token", newAccessToken);
        }
        return newAccessToken;
      })
      .finally(() => {
        // Reset so future 401s can trigger a new refresh
        refreshInFlight = null;
      });
  }
  return refreshInFlight;
};

// Enable API debug logs controlled by runtime config
const __isApiDebugEnabled = (() => {
  try {
    if (typeof VITE_DEBUG === "string")
      return VITE_DEBUG.toLowerCase() === "true";
    const val = (import.meta as any).env?.VITE_DEBUG as string | undefined;
    if (typeof val === "string") return val.toLowerCase() === "true";
  } catch {}
  return (import.meta as any).env?.MODE !== "production";
})();

// Request interceptor for adding auth tokens
api.interceptors.request.use(
  (config: import("axios").InternalAxiosRequestConfig) => {
    const token =
      typeof window !== "undefined" ? localStorage.getItem("auth_token") : null;

    // Skip Authorization for auth routes (login/refresh)
    const fullUrl = `${config.baseURL || ""}${config.url || ""}`;
    const isAuthRoute =
      fullUrl.includes("/auth/login") || fullUrl.includes("/auth/refresh");

    if (token && !isAuthRoute) {
      const headersAny = config.headers as any;
      if (headersAny && typeof headersAny.set === "function") {
        headersAny.set("Authorization", `Bearer ${token}`);
      } else {
        headersAny.Authorization = `Bearer ${token}`;
      }
    }

    if (__isApiDebugEnabled) {
      try {
        // Clone minimal safe snapshot for logging
        const { method, url, baseURL, params, data, headers } = config as any;
        // Avoid dumping huge headers; pick common ones
        const safeHeaders = headers
          ? {
              "Content-Type":
                headers["Content-Type"] || headers["content-type"],
              Accept: headers["Accept"] || headers["accept"],
              Authorization: headers["Authorization"]
                ? "[REDACTED]"
                : undefined,
            }
          : undefined;
        console.debug("[API Request]", {
          method: (method || "").toUpperCase(),
          url: (baseURL || "") + (url || ""),
          params,
          data,
          headers: safeHeaders,
        });
      } catch (e) {
        console.debug("[API Request] log error", e);
      }
    }

    return config;
  },
  (error: any) => {
    if (__isApiDebugEnabled) {
      console.debug("[API Request Error]", error?.message || error);
    }
    return Promise.reject(error);
  },
);

// Response interceptor for error handling
api.interceptors.response.use(
  (response: import("axios").AxiosResponse) => {
    if (__isApiDebugEnabled) {
      try {
        const { status, config, data } = response as any;
        console.debug("[API Response]", {
          method: (config?.method || "").toUpperCase(),
          url: (config?.baseURL || "") + (config?.url || ""),
          status,
          data,
        });
      } catch (e) {
        console.debug("[API Response] log error", e);
      }
    }
    return response;
  },
  async (error: any) => {
    const originalRequest: any = error.config;

    const reqUrl = (originalRequest?.url || "") as string;
    const isAuthRoute =
      reqUrl.includes("/auth/login") || reqUrl.includes("/auth/refresh");
    if (
      error.response?.status === 401 &&
      !originalRequest?._retry &&
      !isAuthRoute
    ) {
      originalRequest._retry = true;
      try {
        const refreshToken =
          typeof window !== "undefined"
            ? localStorage.getItem("refresh_token")
            : null;
        if (!refreshToken) throw new Error("Missing refresh token");

        // Ensure single-flight refresh
        const newAccessToken = await performTokenRefresh(refreshToken);

        // Update header and retry original request
        originalRequest.headers = originalRequest.headers || {};
        const headersAny = originalRequest.headers as any;
        if (headersAny && typeof headersAny.set === "function") {
          headersAny.set("Authorization", `Bearer ${newAccessToken}`);
        } else {
          headersAny.Authorization = `Bearer ${newAccessToken}`;
        }
        return api(originalRequest);
      } catch (refreshErr) {
        // Clear tokens and redirect to login
        if (typeof window !== "undefined") {
          localStorage.removeItem("auth_token");
          localStorage.removeItem("refresh_token");
          window.location.href = "/login";
        }
        return Promise.reject(refreshErr);
      }
    }

    if (__isApiDebugEnabled) {
      try {
        if (error.response) {
          const { status, data, config } = error.response as any;
          console.debug("[API Response Error]", {
            method: (config?.method || "").toUpperCase(),
            url: (config?.baseURL || "") + (config?.url || ""),
            status,
            data,
          });
        } else {
          console.debug("[API Response Error]", error?.message || error);
        }
      } catch (e) {
        console.debug("[API Response Error] log error", e);
      }
    }

    return Promise.reject(error);
  },
);

/**
 * Script API functions
 */
export const scriptApi = {
  /**
   * Get paginated list of scripts with optional filtering
   */
  getScripts: async (
    filters: ScriptFilters = {},
  ): Promise<ScriptListResponse> => {
    const params = new URLSearchParams();

    if (filters.page) params.append("page", filters.page.toString());
    if (filters.page_size)
      params.append("page_size", filters.page_size.toString());
    if (filters.campaign_id)
      params.append("campaign_id", filters.campaign_id.toString());
    if (filters.search) params.append("search", filters.search);
    if (filters.published_only !== undefined)
      params.append("published_only", filters.published_only.toString());

    const response = await api.get(`/scripts?${params.toString()}`);
    return response.data;
  },

  /**
   * Get script by ID
   */
  getScript: async (id: number): Promise<Script> => {
    const response = await api.get(`/scripts/${id}`);
    return response.data;
  },

  /**
   * Create a new script
   */
  createScript: async (data: ScriptCreate): Promise<Script> => {
    const response = await api.post("/scripts", data);
    return response.data;
  },

  /**
   * Update an existing script
   */
  updateScript: async (id: number, data: ScriptUpdate): Promise<Script> => {
    const response = await api.put(`/scripts/${id}`, data);
    return response.data;
  },

  /**
   * Delete a script
   */
  deleteScript: async (id: number): Promise<void> => {
    await api.delete(`/scripts/${id}`);
  },

  /**
   * Perform version control operations on a script
   */
  versionControl: async (
    id: number,
    action: VersionControlAction,
  ): Promise<Script> => {
    const response = await api.post(`/scripts/${id}/version-control`, action);
    return response.data;
  },

  /**
   * Duplicate a published script into a draft
   */
  duplicate: async (
    id: number,
    data: ScriptDuplicateRequest,
  ): Promise<Script> => {
    const response = await api.post(`/scripts/${id}/duplicate`, data);
    return response.data;
  },

  /**
   * Approve a draft script (publish)
   */
  approve: async (id: number, data: ScriptApproveRequest): Promise<Script> => {
    const response = await api.post(`/scripts/${id}/approve`, data);
    return response.data;
  },

  /**
   * Reject a draft script (archive)
   */
  reject: async (id: number, data: ScriptRejectRequest): Promise<Script> => {
    const response = await api.post(`/scripts/${id}/reject`, data);
    return response.data;
  },
};

/**
 * Variables API functions
 */
export const variableApi = {
  getVariables: async (
    filters: VariableFilters = {},
  ): Promise<VariableListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.page_size)
      params.append("page_size", filters.page_size.toString());
    if (filters.search) params.append("search", filters.search);
    if (filters.db_name) params.append("db_name", filters.db_name);
    if (filters.transform) params.append("transform", filters.transform);
    const response = await api.get(`/variables?${params.toString()}`);
    return response.data;
  },

  getVariable: async (id: number): Promise<Variable> => {
    const response = await api.get(`/variables/${id}`);
    return response.data;
  },

  createVariable: async (data: VariableCreate): Promise<Variable> => {
    const response = await api.post("/variables", data);
    return response.data;
  },

  updateVariable: async (
    id: number,
    data: VariableUpdate,
  ): Promise<Variable> => {
    const response = await api.put(`/variables/${id}`, data);
    return response.data;
  },

  deleteVariable: async (id: number): Promise<void> => {
    await api.delete(`/variables/${id}`);
  },
};

/**
 * Client API functions
 */
export const clientApi = {
  getClients: async (
    filters: ClientFilters = {},
  ): Promise<ClientListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.page_size)
      params.append("page_size", filters.page_size.toString());
    if (filters.search) params.append("search", filters.search);
    const response = await api.get(`/clients?${params.toString()}`);
    return response.data;
  },

  getClient: async (id: number): Promise<Client> => {
    const response = await api.get(`/clients/${id}`);
    return response.data;
  },

  createClient: async (data: ClientCreate): Promise<Client> => {
    const response = await api.post("/clients", data);
    return response.data;
  },

  updateClient: async (id: number, data: ClientUpdate): Promise<Client> => {
    const response = await api.put(`/clients/${id}`, data);
    return response.data;
  },

  deleteClient: async (id: number): Promise<void> => {
    await api.delete(`/clients/${id}`);
  },
};

export const campaignApi = {
  getCampaigns: async (
    filters: CampaignFilters = {},
  ): Promise<CampaignListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.page_size)
      params.append("page_size", filters.page_size.toString());
    if (filters.client_id)
      params.append("client_id", filters.client_id.toString());
    if (filters.search) params.append("search", filters.search);
    const response = await api.get(`/campaigns?${params.toString()}`);
    return response.data;
  },

  getCampaign: async (id: number): Promise<Campaign> => {
    const response = await api.get(`/campaigns/${id}`);
    return response.data;
  },

  createCampaign: async (data: CampaignCreate): Promise<Campaign> => {
    const response = await api.post("/campaigns", data);
    return response.data;
  },

  updateCampaign: async (
    id: number,
    data: CampaignUpdate,
  ): Promise<Campaign> => {
    const response = await api.put(`/campaigns/${id}`, data);
    return response.data;
  },

  deleteCampaign: async (id: number): Promise<void> => {
    await api.delete(`/campaigns/${id}`);
  },
};

export const quietPeriodApi = {
  getQuietPeriods: async (
    filters: QuietPeriodFilters = {},
  ): Promise<QuietPeriodListResponse> => {
    const params = new URLSearchParams();
    if (filters.page)
      params.append("page", (filters.page as number).toString());
    if (filters.page_size)
      params.append("page_size", (filters.page_size as number).toString());
    if (filters.search) params.append("search", filters.search);
    const response = await api.get(`/quiet-periods?${params.toString()}`);
    return response.data;
  },

  getQuietPeriod: async (id: number): Promise<QuietPeriod> => {
    const response = await api.get(`/quiet-periods/${id}`);
    return response.data;
  },

  createQuietPeriod: async (data: QuietPeriodCreate): Promise<QuietPeriod> => {
    const response = await api.post("/quiet-periods", data);
    return response.data;
  },

  updateQuietPeriod: async (
    id: number,
    data: QuietPeriodUpdate,
  ): Promise<QuietPeriod> => {
    const response = await api.put(`/quiet-periods/${id}`, data);
    return response.data;
  },

  deleteQuietPeriod: async (id: number): Promise<void> => {
    await api.delete(`/quiet-periods/${id}`);
  },
};

export const httpConfigApi = {
  getConfigs: async (
    filters: HttpRequestConfigFilters = {},
  ): Promise<HttpRequestConfigListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.page_size)
      params.append("page_size", filters.page_size.toString());
    if (filters.search) params.append("search", filters.search);
    const response = await api.get(`/http-configs?${params.toString()}`);
    return response.data;
  },
  getConfig: async (id: number): Promise<HttpRequestConfig> => {
    const response = await api.get(`/http-configs/${id}`);
    return response.data;
  },
  createConfig: async (
    data: HttpRequestConfigCreate,
  ): Promise<HttpRequestConfig> => {
    const response = await api.post("/http-configs", data);
    return response.data;
  },
  updateConfig: async (
    id: number,
    data: HttpRequestConfigUpdate,
  ): Promise<HttpRequestConfig> => {
    const response = await api.put(`/http-configs/${id}`, data);
    return response.data;
  },
  deleteConfig: async (id: number): Promise<void> => {
    await api.delete(`/http-configs/${id}`);
  },
};

export const httpEndpointApi = {
  getEndpoints: async (
    filters: HttpRequestEndpointFilters = {},
  ): Promise<HttpRequestEndpointListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.page_size)
      params.append("page_size", filters.page_size.toString());
    if (filters.search) params.append("search", filters.search);
    if (filters.config_id)
      params.append("config_id", filters.config_id.toString());
    const response = await api.get(`/http-endpoints?${params.toString()}`);
    return response.data;
  },
  getEndpoint: async (id: number): Promise<HttpRequestEndpoint> => {
    const response = await api.get(`/http-endpoints/${id}`);
    return response.data;
  },
  createEndpoint: async (
    data: HttpRequestEndpointCreate,
  ): Promise<HttpRequestEndpoint> => {
    const response = await api.post("/http-endpoints", data);
    return response.data;
  },
  updateEndpoint: async (
    id: number,
    data: HttpRequestEndpointUpdate,
  ): Promise<HttpRequestEndpoint> => {
    const response = await api.put(`/http-endpoints/${id}`, data);
    return response.data;
  },
  deleteEndpoint: async (id: number): Promise<void> => {
    await api.delete(`/http-endpoints/${id}`);
  },
};

export const nodeGroupApi = {
  getGroups: async (
    filters: { page?: number; page_size?: number; search?: string } = {},
  ): Promise<import("../types/node_group").NodeGroupListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append("page", filters.page.toString());
    if (filters.page_size)
      params.append("page_size", filters.page_size.toString());
    if (filters.search) params.append("search", filters.search);
    const response = await api.get(`/node-groups?${params.toString()}`);
    return response.data;
  },
  getGroup: async (
    id: number,
  ): Promise<import("../types/node_group").NodeGroup> => {
    const response = await api.get(`/node-groups/${id}`);
    return response.data;
  },
  getGroupTransitions: async (
    id: number,
  ): Promise<import("../types/node_group").NodeGroupTransitionListResponse> => {
    const response = await api.get(`/node-groups/${id}/transitions`);
    return response.data;
  },
  createGroup: async (
    data: import("../types/node_group").NodeGroupCreate,
  ): Promise<import("../types/node_group").NodeGroup> => {
    const response = await api.post("/node-groups", data);
    return response.data;
  },
  updateGroup: async (
    id: number,
    data: import("../types/node_group").NodeGroupUpdate,
  ): Promise<import("../types/node_group").NodeGroup> => {
    const response = await api.put(`/node-groups/${id}`, data);
    return response.data;
  },
  deleteGroup: async (id: number): Promise<void> => {
    await api.delete(`/node-groups/${id}`);
  },
  // New: transition CRUD
  createTransition: async (
    groupId: number,
    data: import("../types/node_group").NodeGroupTransitionCreate,
  ): Promise<import("../types/node_group").NodeGroupTransition> => {
    const response = await api.post(
      `/node-groups/${groupId}/transitions`,
      data,
    );
    return response.data;
  },
  updateTransition: async (
    groupId: number,
    transitionId: number,
    data: import("../types/node_group").NodeGroupTransitionUpdate,
  ): Promise<import("../types/node_group").NodeGroupTransition> => {
    const response = await api.put(
      `/node-groups/${groupId}/transitions/${transitionId}`,
      data,
    );
    return response.data;
  },
  deleteTransition: async (
    groupId: number,
    transitionId: number,
  ): Promise<void> => {
    await api.delete(`/node-groups/${groupId}/transitions/${transitionId}`);
  },
  // New: condition rules CRUD under a transition
  listConditionRules: async (
    groupId: number,
    transitionId: number,
  ): Promise<import("../types/node_group").ConditionRuleListResponse> => {
    const response = await api.get(
      `/node-groups/${groupId}/transitions/${transitionId}/condition-rules`,
    );
    return response.data;
  },
  createConditionRule: async (
    groupId: number,
    transitionId: number,
    data: import("../types/node_group").ConditionRuleCreate,
  ): Promise<import("../types/node_group").ConditionRule> => {
    const response = await api.post(
      `/node-groups/${groupId}/transitions/${transitionId}/condition-rules`,
      data,
    );
    return response.data;
  },
  updateConditionRule: async (
    groupId: number,
    transitionId: number,
    ruleId: number,
    data: import("../types/node_group").ConditionRuleUpdate,
  ): Promise<import("../types/node_group").ConditionRule> => {
    const response = await api.put(
      `/node-groups/${groupId}/transitions/${transitionId}/condition-rules/${ruleId}`,
      data,
    );
    return response.data;
  },
  deleteConditionRule: async (
    groupId: number,
    transitionId: number,
    ruleId: number,
  ): Promise<void> => {
    await api.delete(
      `/node-groups/${groupId}/transitions/${transitionId}/condition-rules/${ruleId}`,
    );
  },
};

export const scriptTransitionsApi = {
  getTransitions: async (
    scriptId: number,
  ): Promise<{
    transitions: Array<{
      id: number;
      current_node_id: number;
      next_node_id: number;
    }>;
  }> => {
    const response = await api.get(`/scripts/${scriptId}/transitions`);
    return response.data;
  },
  createTransition: async (
    scriptId: number,
    data: {
      current_node_id: number;
      next_node_id: number;
      is_initial?: boolean;
    },
  ): Promise<{ id: number; current_node_id: number; next_node_id: number }> => {
    const response = await api.post(`/scripts/${scriptId}/transitions`, data);
    return response.data;
  },
  updateTransition: async (
    scriptId: number,
    transitionId: number,
    data: { next_node_id: number; is_initial?: boolean },
  ): Promise<{ id: number; current_node_id: number; next_node_id: number }> => {
    const response = await api.put(
      `/scripts/${scriptId}/transitions/${transitionId}`,
      data,
    );
    return response.data;
  },
  deleteTransition: async (
    scriptId: number,
    transitionId: number,
  ): Promise<void> => {
    await api.delete(`/scripts/${scriptId}/transitions/${transitionId}`);
  },
  // Condition rules under script transitions
  listConditionRules: async (
    scriptId: number,
    transitionId: number,
  ): Promise<import("../types/node_group").ConditionRuleListResponse> => {
    const response = await api.get(
      `/scripts/${scriptId}/transitions/${transitionId}/condition-rules`,
    );
    return response.data;
  },
  createConditionRule: async (
    scriptId: number,
    transitionId: number,
    data: import("../types/node_group").ConditionRuleCreate,
  ): Promise<import("../types/node_group").ConditionRule> => {
    const response = await api.post(
      `/scripts/${scriptId}/transitions/${transitionId}/condition-rules`,
      data,
    );
    return response.data;
  },
  updateConditionRule: async (
    scriptId: number,
    transitionId: number,
    ruleId: number,
    data: import("../types/node_group").ConditionRuleUpdate,
  ): Promise<import("../types/node_group").ConditionRule> => {
    const response = await api.put(
      `/scripts/${scriptId}/transitions/${transitionId}/condition-rules/${ruleId}`,
      data,
    );
    return response.data;
  },
  deleteConditionRule: async (
    scriptId: number,
    transitionId: number,
    ruleId: number,
  ): Promise<void> => {
    await api.delete(
      `/scripts/${scriptId}/transitions/${transitionId}/condition-rules/${ruleId}`,
    );
  },
};

export const conditionRulesApi = {
  list: async (
    filters: {
      search?: string;
      global_only?: boolean;
      page?: number;
      page_size?: number;
    } = {},
  ): Promise<import("../types/node_group").ConditionRuleListResponse> => {
    const params = new URLSearchParams();
    if (filters.search) params.append("search", filters.search);
    if (filters.global_only !== undefined)
      params.append("global_only", String(filters.global_only));
    if (filters.page) params.append("page", String(filters.page));
    if (filters.page_size)
      params.append("page_size", String(filters.page_size));
    const response = await api.get(`/condition-rules?${params.toString()}`);
    return response.data;
  },
  create: async (
    payload: import("../types/node_group").ConditionRuleCreate,
  ): Promise<import("../types/node_group").ConditionRule> => {
    const response = await api.post(`/condition-rules`, payload);
    return response.data;
  },
  get: async (
    id: number,
  ): Promise<import("../types/node_group").ConditionRule> => {
    const response = await api.get(`/condition-rules/${id}`);
    return response.data;
  },
  update: async (
    id: number,
    data: import("../types/node_group").ConditionRuleUpdate,
  ): Promise<import("../types/node_group").ConditionRule> => {
    const response = await api.put(`/condition-rules/${id}`, data);
    return response.data;
  },
  remove: async (id: number): Promise<void> => {
    await api.delete(`/condition-rules/${id}`);
  },
};

export const actionsApi = {
  list: async (
    nodeId: number,
    page = 1,
    page_size = 200,
  ): Promise<import("../types/node_action").NodeActionListResponse> => {
    const params = new URLSearchParams({
      page: String(page),
      page_size: String(page_size),
    });
    const response = await api.get(
      `/nodes/${nodeId}/actions?${params.toString()}`,
    );
    return response.data;
  },
  create: async (
    payload: import("../types/node_action").NodeActionCreate,
  ): Promise<import("../types/node_action").NodeAction> => {
    const response = await api.post(
      `/nodes/${payload.node_id}/actions`,
      payload,
    );
    return response.data;
  },
  update: async (
    actionId: number,
    data: import("../types/node_action").NodeActionUpdate,
  ): Promise<import("../types/node_action").NodeAction> => {
    const response = await api.put(`/actions/${actionId}`, data);
    return response.data;
  },
  remove: async (actionId: number): Promise<void> => {
    await api.delete(`/actions/${actionId}`);
  },

  // Email actions
  getEmailConfig: async (
    actionId: number,
  ): Promise<import("../types/node_action").EmailActionConfig> => {
    const response = await api.get(`/actions/${actionId}/email`);
    return response.data;
  },
  setEmailConfig: async (
    actionId: number,
    cfg: import("../types/node_action").EmailActionConfig,
  ): Promise<import("../types/node_action").EmailActionConfig> => {
    const response = await api.put(`/actions/${actionId}/email`, cfg);
    return response.data;
  },

  // SMS actions
  getSmsConfig: async (
    actionId: number,
  ): Promise<import("../types/node_action").SmsActionConfig> => {
    const response = await api.get(`/actions/${actionId}/sms`);
    return response.data;
  },
  setSmsConfig: async (
    actionId: number,
    cfg: import("../types/node_action").SmsActionConfig,
  ): Promise<import("../types/node_action").SmsActionConfig> => {
    const response = await api.put(`/actions/${actionId}/sms`, cfg);
    return response.data;
  },

  // Run query actions
  getRunQueryConfig: async (
    actionId: number,
  ): Promise<import("../types/node_action").RunQueryActionConfig> => {
    const response = await api.get(`/actions/${actionId}/run_query`);
    return response.data;
  },
  setRunQueryConfig: async (
    actionId: number,
    cfg: import("../types/node_action").RunQueryActionConfig,
  ): Promise<import("../types/node_action").RunQueryActionConfig> => {
    const response = await api.put(`/actions/${actionId}/run_query`, cfg);
    return response.data;
  },

  // Standalone actions management
  listEmailActions: async (): Promise<
    import("../types/node_action").EmailAction[]
  > => {
    const response = await api.get(`/email-actions`);
    return response.data;
  },
  listSmsActions: async (): Promise<
    import("../types/node_action").SmsAction[]
  > => {
    const response = await api.get(`/sms-actions`);
    return response.data;
  },
  listRunQueryActions: async (): Promise<
    import("../types/node_action").RunQueryAction[]
  > => {
    const response = await api.get(`/run-query-actions`);
    return response.data;
  },

  // Delete standalone actions
  deleteEmailAction: async (actionId: number): Promise<void> => {
    await api.delete(`/email-actions/${actionId}`);
  },
  deleteSmsAction: async (actionId: number): Promise<void> => {
    await api.delete(`/sms-actions/${actionId}`);
  },
  deleteRunQueryAction: async (actionId: number): Promise<void> => {
    await api.delete(`/run-query-actions/${actionId}`);
  },
};

export const templateApi = {
  getTemplates: async (
    filters: import("../types/template").ActionTemplateFilters = {},
  ): Promise<import("../types/template").ActionTemplateListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append("page", String(filters.page));
    if (filters.page_size)
      params.append("page_size", String(filters.page_size));
    if (filters.search) params.append("search", filters.search);
    if (filters.template_type)
      params.append("template_type", filters.template_type);
    const response = await api.get(`/templates?${params.toString()}`);
    return response.data;
  },
  getTemplate: async (
    id: number,
  ): Promise<import("../types/template").ActionTemplate> => {
    const response = await api.get(`/templates/${id}`);
    return response.data;
  },
  createTemplate: async (
    data: import("../types/template").ActionTemplateCreate,
  ): Promise<import("../types/template").ActionTemplate> => {
    const response = await api.post("/templates", data);
    return response.data;
  },
  updateTemplate: async (
    id: number,
    data: import("../types/template").ActionTemplateUpdate,
  ): Promise<import("../types/template").ActionTemplate> => {
    const response = await api.put(`/templates/${id}`, data);
    return response.data;
  },
  deleteTemplate: async (id: number): Promise<void> => {
    await api.delete(`/templates/${id}`);
  },
};

export const auditApi = {
  getLastChange: async (
    resource: string,
    id: number,
  ): Promise<import("../types/audit").AuditEntry | null> => {
    const params = new URLSearchParams({ resource, id: String(id) });
    const response = await api.get(`/audit/last?${params.toString()}`);
    return response.data || null;
  },
  getLastTwo: async (
    resource: string,
    id: number,
  ): Promise<import("../types/audit").AuditEntry[]> => {
    const params = new URLSearchParams({ resource, id: String(id) });
    const response = await api.get(`/audit/last-two?${params.toString()}`);
    return Array.isArray(response.data) ? response.data : [];
  },
  // New: list flow audits with filters
  listFlow: async (
    scriptId: number,
    filters: import("../types/audit").FlowAuditFilters = {},
  ): Promise<import("../types/audit").AuditListResponse> => {
    const params = new URLSearchParams();
    params.append("script_id", String(scriptId));
    if (filters.page) params.append("page", String(filters.page));
    if (filters.page_size)
      params.append("page_size", String(filters.page_size));
    if (filters.category) params.append("category", String(filters.category));
    if (filters.search) params.append("search", String(filters.search));
    if (filters.user_email)
      params.append("user_email", String(filters.user_email));
    if (filters.date_from)
      params.append("date_from", String(filters.date_from));
    if (filters.date_to) params.append("date_to", String(filters.date_to));
    const response = await api.get(`/audit/flow?${params.toString()}`);
    return response.data as import("../types/audit").AuditListResponse;
  },
};

export const trackEventsApi = {
  list: async (
    filters: { page?: number; page_size?: number; search?: string } = {},
  ): Promise<import("../types/track").TrackEventListResponse> => {
    const params = new URLSearchParams();
    if (filters.page) params.append("page", String(filters.page));
    if (filters.page_size)
      params.append("page_size", String(filters.page_size));
    if (filters.search) params.append("search", String(filters.search));
    const response = await api.get(`/track-events?${params.toString()}`);
    return response.data;
  },
  getForScript: async (
    scriptId: number,
  ): Promise<import("../types/track").ScriptTrackEventsResponse> => {
    const response = await api.get(`/scripts/${scriptId}/track-events`);
    return response.data;
  },
  setForScript: async (
    scriptId: number,
    trackEventIds: number[],
  ): Promise<import("../types/track").ScriptTrackEventsResponse> => {
    const response = await api.put(`/scripts/${scriptId}/track-events`, {
      track_event_ids: trackEventIds,
    });
    return response.data;
  },
};

export const usersApi = {
  listPermissions: async (): Promise<
    Array<{ key: string; description: string }>
  > => {
    const response = await api.get("/users/permissions");
    return response.data || [];
  },
  updateUser: async (
    id: number,
    data: Partial<{ email: string; password: string; permissions: string[] }>,
  ): Promise<any> => {
    const response = await api.put(`/users/${id}`, data);
    return response.data;
  },
  deleteUser: async (id: number): Promise<void> => {
    await api.delete(`/users/${id}`);
  },
};

export default api;
